import {
    Checkbox,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemIcon,
    ListItemSecondaryAction,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import { formatDistance } from 'date-fns';
import {
    RecordContextProvider,
    SimpleListLoading,
    useListContext,
} from 'react-admin';
import { Link } from 'react-router-dom';

import { Status } from '@app/misc/Status';
import { getFullName } from '@domain/member';
import { MemberSummary } from '@wwf/sdk';
import { MemberAvatar } from '../show/MemberAvatar';
import { TagsList } from '../tags/TagsList';
import { findMemberTypeLabel } from '../findMemberTypeLabel';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import { MemberEmpty } from './MemberEmpty';
import { getLinkToMember } from '../getLinkToMember';

export const MemberListContent = ({
    noBulkActions,
}: {
    noBulkActions?: boolean;
}) => {
    const { memberTypeLabels } = useConfigurationContext();
    const {
        data: member,
        error,
        isPending,
        onToggleItem,
        selectedIds,
    } = useListContext<MemberSummary>();

    if (isPending) {
        return <SimpleListLoading hasLeftAvatarOrIcon hasSecondaryText />;
    }
    if (error) {
        return null;
    }
    const now = Date.now();

    if (!member?.length) return <MemberEmpty />;

    return (
        <List dense>
            {member.map(member => (
                <RecordContextProvider key={member.id} value={member}>
                    <ListItem disablePadding>
                        <Stack flex={1}>
                            <ListItemButton
                                component={Link}
                                to={getLinkToMember(member.id)}
                                alignItems="center"
                            >
                                {!noBulkActions && (
                                    <ListItemIcon sx={{ minWidth: '2.5em' }}>
                                        <Checkbox
                                            edge="start"
                                            checked={selectedIds.includes(
                                                member.id
                                            )}
                                            tabIndex={-1}
                                            disableRipple
                                            onClick={e => {
                                                e.stopPropagation();
                                                onToggleItem(member.id);
                                            }}
                                        />
                                    </ListItemIcon>
                                )}
                                <ListItemAvatar>
                                    <MemberAvatar />
                                </ListItemAvatar>
                                <ListItemText
                                    primary={getFullName(member)}
                                    secondary={
                                        <>
                                            {findMemberTypeLabel(
                                                memberTypeLabels,
                                                member.type
                                            )}
                                            {member.antennas != null && ' at '}
                                            {member.antennas
                                                ?.map(antenna => antenna.name)
                                                .join(', ')}
                                            &nbsp;&nbsp;
                                            <TagsList limit={5} />
                                        </>
                                    }
                                />
                                {
                                    <ListItemSecondaryAction
                                        sx={{
                                            top: '10px',
                                            transform: 'none',
                                            display: {
                                                xs: 'none',
                                                sm: 'none',
                                                md: 'block',
                                            },
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            {member.nbMissions ?? 0}
                                            {member.nbMissions
                                                ? member.nbMissions > 1
                                                    ? ' missions'
                                                    : ' mission'
                                                : ' missions'}
                                            {' - last activity '}
                                            {(member.lastSeen &&
                                                formatDistance(
                                                    member.lastSeen,
                                                    now
                                                ) + ' ago') ||
                                                'never'}
                                            <Status
                                                status={
                                                    member.status ?? undefined
                                                }
                                            />
                                        </Typography>
                                    </ListItemSecondaryAction>
                                }
                            </ListItemButton>
                            <Divider variant="middle" flexItem />
                        </Stack>
                    </ListItem>
                </RecordContextProvider>
            ))}

            {member.length === 0 && (
                <ListItem>
                    <ListItemText primary="No member found" />
                </ListItem>
            )}
        </List>
    );
};
